import { Component, inject } from '@angular/core';

import { LoadingService } from '../../../core/services/loading.service';

/**
 * Full-screen, interaction-blocking loading overlay.
 *
 * Rendered once at the app root (next to the toast host) and driven entirely by
 * the {@link LoadingService}. While any loading holder is active it covers the
 * whole viewport with a blurred backdrop and a centered spinner, capturing all
 * pointer events so the user cannot interact with the page underneath until the
 * work behind it (typically every in-flight HTTP request) has finished.
 */
@Component({
  selector: 'app-global-loading',
  standalone: true,
  styles: `
    .global-loading {
      position: fixed;
      inset: 0;
      z-index: 2000;
      display: flex;
      align-items: center;
      justify-content: center;
      background: color-mix(in srgb, var(--layout-surface, #ffffff) 55%, transparent);
      backdrop-filter: blur(2px);
      /* Block every interaction with the page underneath. */
      pointer-events: all;
      animation: global-loading-in 120ms ease-out;
    }

    .global-loading__spinner {
      height: 3rem;
      width: 3rem;
      border-radius: 9999px;
      border: 3px solid var(--layout-border, #e2e8f0);
      border-top-color: var(--brand-primary, #2563eb);
      animation: global-loading-spin 720ms linear infinite;
    }

    @keyframes global-loading-spin {
      to {
        transform: rotate(360deg);
      }
    }

    @keyframes global-loading-in {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .global-loading {
        animation: none;
      }

      .global-loading__spinner {
        animation-duration: 1.4s;
      }
    }
  `,
  template: `
    @if (isLoading()) {
      <div class="global-loading" role="status" aria-live="polite" aria-busy="true">
        <span class="global-loading__spinner" aria-hidden="true"></span>
      </div>
    }
  `
})
export class GlobalLoadingComponent {
  private readonly loadingService = inject(LoadingService);

  protected readonly isLoading = this.loadingService.isLoading;
}
